import { rotationForDefaultAssetType } from '@ikegps/core-api';
import { AssetTypeSwatch } from '../../components/asset.type.swatch';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { LineSwatch } from '../../components/line.swatch';
import { addStyles, assetPath } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { classNames } from '../../util/classnames';
import {
	grantedLegendItems,
	type LegendItem,
	legendItemLabel,
	type LegendSwatch,
} from '../topological.editor.map/map.legend';
import {
	type PoleAccuracy,
	poleMeasurementAccuracyTypes,
	TopologicalEditorPointCSS,
} from '../topological.editor.map/map.point/map.point';
import { GenericPointCSS } from '../topological.editor.map/map.point.generic/map.point.generic';
import type { TopologicalEditorMapOverlay } from '../topological.editor.map/topological.editor.map.overlay';
import { CSS, Style } from './map.mode.legend.style';

addStyles(Style);

function lineIcon(color: string): MithrilVNode {
	return m('div', { className: CSS.legendIconLine, style: `background-color: ${color}` });
}

function pointIcon(accuracy: PoleAccuracy, selected: boolean = false): MithrilVNode {
	return m('div', {
		className: classNames(TopologicalEditorPointCSS.container, poleMeasurementAccuracyTypes[accuracy], {
			[TopologicalEditorPointCSS.isSelected]: selected,
		}),
	});
}

function swatchIcon(swatch: LegendSwatch): MithrilVNode {
	switch (swatch.kind) {
		case 'line':
			return lineIcon(swatch.color);
		case 'point':
			return pointIcon(swatch.accuracy, swatch.selected);
		case 'collectionLabel':
			return m(
				'div',
				{ className: classNames(TopologicalEditorPointCSS.title, CSS.legendCollectionLabelOverride) },
				'abc'
			);
		case 'dot':
			// The MidSpan capture dot: the same GenericMapPoint the map draws, at legend scale.
			return m('div', {
				className: GenericPointCSS.container,
				style: { position: 'relative', background: swatch.color },
			});
		case 'device':
			return m('img', { src: assetPath('/images/ike-device.svg'), className: CSS.legendIconDevice });
		case 'lineStyle':
			return LineSwatch.view({ symbol: swatch.symbol, width: 32, height: 12 });
		case 'assetType':
			return AssetTypeSwatch.view({
				icon: swatch.icon,
				color: swatch.color,
				size: 20,
				rotate: rotationForDefaultAssetType(swatch.title),
			});
	}
}

/**
 * Resolves the visibility toggle for a legend row — a static feature (`id`), a line type
 * (`lineTypeKey`), or an asset type (`assetTypeKey`).
 */
function legendToggle(
	overlay: TopologicalEditorMapOverlay | null | undefined,
	item: LegendItem
): { isHidden: boolean; onToggle: () => void } | null {
	if (overlay == null) {
		return null;
	}
	const ov = overlay;
	const id = item.id;
	if (item.toggleable === true && id != null) {
		return { isHidden: !ov.isLegendItemVisible(id), onToggle: () => ov.toggleLegendItem(id) };
	}
	const lineTypeKey = item.lineTypeKey;
	if (lineTypeKey != null) {
		return { isHidden: !ov.isLineTypeVisible(lineTypeKey), onToggle: () => ov.toggleLineType(lineTypeKey) };
	}
	const assetTypeKey = item.assetTypeKey;
	if (assetTypeKey != null) {
		return { isHidden: !ov.isAssetTypeVisible(assetTypeKey), onToggle: () => ov.toggleAssetType(assetTypeKey) };
	}
	return null;
}

export const MapModeLegend = {
	view(overlay?: TopologicalEditorMapOverlay | null, extraItems: LegendItem[] = []) {
		return m(
			'div',
			{ className: CSS.container },
			grantedLegendItems(extraItems).map((item) => {
				const toggle = legendToggle(overlay, item);
				const isHidden = toggle?.isHidden === true;

				const eye = toggle
					? m(
							'button',
							{
								className: CSS.legendEye,
								title: Translate(isHidden ? T.MAPMODE_LEGEND_SHOW_LAYER : T.MAPMODE_LEGEND_HIDE_LAYER),
								onclick: toggle.onToggle,
							},
							Icon.view(isHidden ? MaterialIcon.VISIBILITY_OFF : MaterialIcon.VISIBILITY, {
								className: classNames(CSS.legendEyeIcon, { [CSS.legendEyeIconHidden]: isHidden }),
							})
						)
					: null;

				return m('div', { className: classNames(CSS.legendItem, { [CSS.legendItemHidden]: isHidden }) }, [
					m('div', { className: CSS.legendIcon }, swatchIcon(item.swatch)),
					m('div', { className: CSS.legendText }, legendItemLabel(item)),
					eye,
				]);
			})
		);
	},
};
